/**
 * useSessionTracker
 * Tracks user sessions for DAU/MAU, session duration, and retention metrics.
 *
 * Usage: Mount once at the App root level: useSessionTracker()
 *
 * Behaviour:
 *  - On mount: generates a session token, POSTs to /api/analytics/session-start
 *  - Every 60s:   POSTs a ping to increment page_count
 *  - On unload:   uses navigator.sendBeacon to close the session with duration
 */

import { useEffect, useRef } from "react";
import { useAuth } from "@/lib/authContext";

function getDeviceType(): "mobile" | "tablet" | "desktop" {
    const w = window.innerWidth;
    if (w < 768) return "mobile";
    if (w < 1024) return "tablet";
    return "desktop";
}

function generateToken(): string {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function useSessionTracker() {
    const { user } = useAuth();
    const tokenRef = useRef<string | null>(null);
    const startTimeRef = useRef<number>(Date.now());
    const pageCountRef = useRef<number>(1);

    useEffect(() => {
        const token = generateToken();
        tokenRef.current = token;
        startTimeRef.current = Date.now();
        pageCountRef.current = 1;

        // Gather context
        const country = (user as any)?.country ?? undefined;
        const deviceType = getDeviceType();

        // Register session start  (fire and forget — don't await)
        fetch("/api/analytics/session-start", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ sessionToken: token, deviceType, country }),
            // credentials needed when behind an auth cookie
            credentials: "include",
        }).catch(() => {/* non-fatal */ });

        // Periodic ping removed as it causes too many network requests
        // session duration will be calculated entirely based on unload timestamp

        // On unload: send session-end via sendBeacon (survives page close)
        const handleUnload = () => {
            const durationSeconds = Math.round((Date.now() - startTimeRef.current) / 1000);
            const payload = JSON.stringify({
                sessionToken: token,
                durationSeconds,
                pageCount: pageCountRef.current,
            });

            if (navigator.sendBeacon) {
                navigator.sendBeacon("/api/analytics/session-end", new Blob([payload], { type: "application/json" }));
            } else {
                // Fallback: synchronous fetch (less reliable but better than nothing)
                fetch("/api/analytics/session-end", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: payload,
                    keepalive: true,
                    credentials: "include",
                }).catch(() => { });
            }
        };

        window.addEventListener("beforeunload", handleUnload);
        window.addEventListener("pagehide", handleUnload);

        return () => {
            window.removeEventListener("beforeunload", handleUnload);
            window.removeEventListener("pagehide", handleUnload);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount
}

/**
 * trackRouteEvent
 * Call this imperatively when a route event occurs.
 *
 * @param routeId   UUID of the route
 * @param eventType "view" | "maps_click" | "share"
 */
export async function trackRouteEvent(routeId: string, eventType: "view" | "maps_click" | "share") {
    try {
        await fetch("/api/analytics/route-event", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ routeId, eventType }),
            credentials: "include",
        });
    } catch {
        // Non-fatal — never block the user action
    }
}
